import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { buildQuotationPDF } from './AdminQuotation';
import { DOC_THEMES, loadThemeChoice, saveThemeChoice } from '../print/docThemes';
import MenuButton from '../components/MenuButton';

// ═══════════════════════════════════════════════════════════════
//  QUOTATION PREVIEW — full page, live template switching
// ═══════════════════════════════════════════════════════════════
// The preview is the actual PDF rendered in an iframe, not an HTML mock-up of
// it. That means what is on screen is exactly what downloads and what the
// client receives — there is no second layout to keep in step. Picking a
// template rebuilds the PDF and swaps the iframe source.

export default function QuotationPreview() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [quote, setQuote] = useState(null);
  const [theme, setTheme] = useState(loadThemeChoice);
  // Two buffers. The new template loads into the hidden one and only becomes
  // visible once it has finished rendering, so the document never blanks out
  // mid-switch the way a single iframe does when its src changes.
  const [visibleUrl, setVisibleUrl] = useState('');
  const [pendingUrl, setPendingUrl] = useState('');
  const [building, setBuilding] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Every build makes a blob URL; they leak until revoked, so keep the set and
  // clear it on unmount.
  const urlsRef = useRef(new Set());
  // Mirrors visibleUrl so the build effect can branch without reading state
  // inside an updater (React may invoke those twice in StrictMode).
  const visibleRef = useRef('');

  useEffect(() => {
    let cancelled = false;
    api.get(`/sales-quotations/${id}`)
      .then(r => { if (!cancelled) setQuote(r.data); })
      .catch(() => { if (!cancelled) setError('Could not load this quotation.'); });
    return () => { cancelled = true; };
  }, [id]);

  // Rebuild whenever the quotation loads or the template changes.
  useEffect(() => {
    if (!quote) return;
    let cancelled = false;
    setBuilding(true);
    (async () => {
      try {
        const doc = await buildQuotationPDF(quote, theme);
        if (cancelled) return;
        const url = doc.output('bloburl').toString();
        urlsRef.current.add(url);
        setError('');
        // Nothing on screen yet: show it straight away. Otherwise hand it to
        // the hidden buffer and let onLoad promote it.
        if (visibleRef.current) {
          setPendingUrl(url);
        } else {
          visibleRef.current = url;
          setVisibleUrl(url);
          setBuilding(false);
        }
      } catch {
        if (!cancelled) setError('Could not build the preview.');
      }
    })();
    return () => { cancelled = true; };
  }, [quote, theme]);

  useEffect(() => {
    const urls = urlsRef.current;
    return () => { urls.forEach(u => URL.revokeObjectURL(u)); urls.clear(); };
  }, []);

  // The hidden buffer finished rendering — swap it in and drop the old one.
  const promoteBuffer = () => {
    if (!pendingUrl) return;
    const previous = visibleRef.current;
    visibleRef.current = pendingUrl;
    setVisibleUrl(pendingUrl);
    setPendingUrl('');
    setBuilding(false);
    if (previous && previous !== pendingUrl) {
      URL.revokeObjectURL(previous);
      urlsRef.current.delete(previous);
    }
  };

  const pickTheme = (key) => { setTheme(key); saveThemeChoice(key); };

  const withDoc = async (fn, failMsg) => {
    if (!quote) return;
    setBusy(true);
    try { fn(await buildQuotationPDF(quote, theme)); }
    catch { toast.error(failMsg); }
    finally { setBusy(false); }
  };

  const download = () => withDoc(
    doc => doc.save(`${quote.quotationNo || 'quotation'}-${theme}.pdf`),
    'Could not generate PDF');

  const share = (channel) => withDoc(async (doc) => {
    const message = `Hi ${quote.clientName || 'there'}, here is your quotation ${quote.quotationNo || ''}`
      + ` for ${quote.projectType || 'your project'}. - Wedoo Interiors`;
    // The native share sheet is the only way to hand the file straight to
    // WhatsApp or Mail; otherwise fall back to a link plus a saved copy.
    try {
      const file = new File([doc.output('blob')], `${quote.quotationNo || 'quotation'}.pdf`, { type: 'application/pdf' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `Quotation ${quote.quotationNo || ''}`, text: message });
        return;
      }
    } catch (err) {
      if (err && err.name === 'AbortError') return; // cancelled by the user
    }
    doc.save(`${quote.quotationNo || 'quotation'}.pdf`);
    const note = ' The PDF has just been downloaded on this device — please attach it here.';
    if (channel === 'whatsapp') {
      const params = new URLSearchParams();
      const phone = (quote.clientMobile || '').replace(/\D/g, '');
      if (phone) params.set('phone', phone.length === 10 ? `91${phone}` : phone);
      params.set('text', message + note);
      window.open(`https://api.whatsapp.com/send?${params.toString()}`, '_blank');
    } else {
      const subject = `Quotation ${quote.quotationNo || ''} - Wedoo Interiors`;
      window.location.href = `mailto:${quote.clientEmail || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message + note)}`;
    }
  }, 'Could not share');

  if (error && !quote) return <p className="admin-empty">{error}</p>;
  if (!quote) return <p className="admin-empty">Loading...</p>;

  return (
    <div className="qp">
      <div className="qp-head">
        <div>
          <button className="qp-back" onClick={() => navigate('/admin/quotation')}>← Back to Quotations</button>
          <p className="qp-eyebrow">Preview</p>
          <h1 className="qp-title">
            {quote.quotationNo || 'Quotation'}
            {quote.clientName && <span style={{ color: 'var(--muted)', fontSize: '1rem' }}> · {quote.clientName}</span>}
          </h1>
        </div>
        <div className="qp-actions">
          <Link to={`/admin/quotation/${id}`} className="btn btn-dark" style={{ fontSize: '0.7rem' }}>✎ Edit</Link>
          <MenuButton
            icon="📤" label="Share" disabled={busy}
            items={[
              { icon: '📱', label: 'WhatsApp', onClick: () => share('whatsapp') },
              { icon: '✉️', label: 'Email', onClick: () => share('email') },
            ]} />
          <MenuButton
            icon="⬇" label="Download" disabled={busy} variant="btn-gold"
            items={[
              { icon: '📄', label: 'PDF', onClick: download },
            ]} />
        </div>
      </div>

      <div className="qp-body">
        {/* Template rail — picking one rebuilds the PDF beside it. */}
        <aside className="qp-rail">
          <p className="qp-rail-title">Template</p>
          {DOC_THEMES.map(t => (
            <button
              key={t.key}
              onClick={() => pickTheme(t.key)}
              title={t.description}
              className={`qp-tpl${theme === t.key ? ' is-active' : ''}`}>
              <span className="qp-dot" style={{ background: t.swatch }} />
              <span className="qp-tpl-name">{t.label}</span>
            </button>
          ))}
        </aside>

        <div className="qp-stage">
          {building && <div className="qp-loading">Rendering {DOC_THEMES.find(t => t.key === theme)?.label || ''}…</div>}
          {error && !building && <div className="qp-loading">{error}</div>}
          {visibleUrl && (
            <iframe
              title={`Quotation ${quote.quotationNo || ''} preview`}
              src={visibleUrl}
              className="qp-frame"
            />
          )}
          {/* Back buffer: renders the next template off-screen, then swaps in. */}
          {pendingUrl && (
            <iframe
              title="Loading next template"
              src={pendingUrl}
              className="qp-frame qp-frame-back"
              aria-hidden="true"
              onLoad={promoteBuffer}
            />
          )}
        </div>
      </div>
    </div>
  );
}
